import React, { useEffect, useState } from 'react';
import { PlayCircle, Pencil, Trash2, Bot, PlusCircle, RocketIcon, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ingestionService } from '../../../../components/api/ingestionService';
import CreditEvaluationPopup from './AgentStudio/AgentSampleOutput';
import { agentService } from './services/agentService';
import RenameModal from '../../common/RenameModal';
import AddGroupModal from './AddGroupModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import IconViewer from '../../common/IconViewer';
import EmojiIcon from '../../common/IconViewer';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';

// --- Type Interfaces ---
interface AgentGroup {
  id: string;
  name: string;
  iconUrl: string;
}

interface Agent {
  id: string;
  agentGroupId: string;
  title: string;
  key: string;
  description: string;
  iconUrl: string;
  tags: string[];
  inputType: number;
  outputType: number;
  isValidated: boolean;
}

const Agents = () => {
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
  const [agentList, setAgentList] = useState<Agent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [evalResults, setEvalResults] = useState(null);
  const [showEvalPopup, setShowEvalPopup] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; currentName: string } | null>(null);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [confirmDeleteAgentId, setConfirmDeleteAgentId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentModalAgentId, setAssignmentModalAgentId] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [groups, agents] = await Promise.all([
          agentService.getAgentGroups(),
          agentService.getAgentList()
        ]);
        setAgentGroups(groups);
        setAgentList(agents);
      } catch (error) {
        console.error('Failed to load agent data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const allGroups = ['All', ...agentGroups.map((g) => g.name)];
  const groupId = agentGroups.find((g) => g.name === selectedGroup)?.id;

  const filteredAgents = agentList.filter((agent) => {
    const inGroup = selectedGroup === 'All' || agent.agentGroupId === groupId;
    const matchesSearch =
      agent.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return inGroup && matchesSearch;
  });

  const handleRenameClick = (agent: Agent) => {
    setRenameTarget({ id: agent.id, currentName: agent.title });
    setShowRenameModal(true);
  };

  const handleSaveRename = async ({
    id,
    newName,
  }: {
    id: string;
    newName: string;
    module: string;
  }) => {
    try {
      await agentService.renameAgent({
        tenantId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        agentId: id,
        newTitle: newName,
      });
      setShowRenameModal(false);
      setRenameTarget(null);
      const updated = await agentService.getAgentList();
      setAgentList(updated);
    } catch (error) {
      console.error('Rename failed', error);
    }
  };


  const handleSaveGroup = async (groupName: string) => {
    try {
      await agentService.createAgentGroup({
        tenantId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        name: groupName,
        description: '', // optional, can allow user to input this later
        iconUrl: '',     // optional, can be static or selectable later
        createdBy: 'admin@example.com', // or get from auth/user context
      });

      const updatedGroups = await agentService.getAgentGroups();
      setAgentGroups(updatedGroups);
      setShowAddGroupModal(false);
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };
  const handleDeleteGroup = async (groupId: string) => {
    try {
      await agentService.deleteAgentGroup(groupId);
      const updatedGroups = await agentService.getAgentGroups();
      setAgentGroups(updatedGroups);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await agentService.deleteAgent(agentId);
      const updated = await agentService.getAgentList();
      setAgentList(updated);
    } catch (error) {
      console.error('Failed to delete agent:', error);
    }
  };

  return (
    <div className="flex h-screen bg-neutral-950 text-sm text-gray-300">
      {/* Sidebar */}
      <aside className="w-64 border-r border-neutral-800 flex flex-col">
        <div className="p-4 flex items-center justify-between border-b border-neutral-800">
          <h2 className="text-white font-semibold text-sm">Function groups</h2>
          <button title="Add Group" className="text-gray-400 hover:text-white"
            onClick={() => setShowAddGroupModal(true)}>
            <PlusCircle size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-2">
          {agentGroups.map((group) => {
            const isSelected = selectedGroup === group.name;
            const agentsInGroup = agentList.filter((a) => a.agentGroupId === group.id);

            return (
              <div
                key={group.id}
                className="group flex items-center justify-between px-3 py-2 rounded-md hover:bg-neutral-800 transition"
              >
                <button
                  onClick={() => setSelectedGroup(group.name)}
                  className={`flex items-center w-full text-left truncate ${isSelected ? 'text-white' : 'text-gray-300'
                    }`}
                >
                  <Bot size={16} className="mr-2" />
                  <span className="truncate">{group.name}</span>
                </button>

                {agentsInGroup.length === 0 && (
                  <button
                    onClick={() => setConfirmDeleteId(group.id)}
                    title="Delete Group"
                    className="ml-2 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Functions <span className="text-gray-400 font-normal">({selectedGroup})</span>
              <p className="text-sm text-gray-400">Manage and configure your AI functions and models.</p>
            </h2>
          </div>

          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search functions..."
              className="bg-neutral-800 border border-neutral-700 text-white px-3 py-1 rounded-md text-sm w-64"
            />
            <button
              onClick={() => navigate('/portal/agentStudio')}
              className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Agent Functions Studio
            </button>
          </div>
        </div>

        {/* Loader / Empty / Agents */}
        {isLoading ? (
          <div className="text-center text-gray-400 py-10">Loading functions...</div>
        ) : filteredAgents.length === 0 ? (
          <div className="text-gray-500 text-center py-10">No functions found in this group.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className="bg-neutral-900 border border-neutral-800 rounded-lg p-4 hover:border-indigo-500 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-white font-medium text-sm">
                      <EmojiIcon iconUrl={agent.iconUrl} size={15} />   {agent.title}</h3>
                    <p className="text-xs text-gray-400 mt-1">{agent.description}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-1 text-xs text-gray-300">
                  <div><span className="text-gray-500">Key:</span> {agent.key}</div>
                  <div><span className="text-gray-500">Input Type:</span> {agent.inputType}</div>
                  <div><span className="text-gray-500">Output Type:</span> {agent.outputType}</div>
                  <div>
                    <span className="text-gray-500">Status:</span>{' '}
                    <span className={agent.isValidated ? 'text-green-400' : 'text-red-400'}>
                      {agent.isValidated ? 'Validated' : 'Not Validated'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Tags:</span>{' '}
                    {agent.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block bg-neutral-800 text-gray-300 px-2 py-0.5 rounded-full text-[11px] mr-1"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">

                  <button
                    className="text-indigo-400 hover:text-indigo-300 transition-colors"
                    title="Manage Permissions & Access"
                    onClick={() => {
                      setAssignmentModalAgentId(agent.id);
                      setShowAssignmentModal(true);
                    }}
                  >
                    <UserCog className="w-4 h-4" />
                  </button>
                  <button
                    className="text-green-400 hover:text-green-300"
                    title="Run"
                    onClick={async () => {
                      if (agent.key === '-') {
                        setShowEvalPopup(true);
                        setEvalResults(null);
                        try {
                          const res = await ingestionService.runCreditEvaluation();
                          setEvalResults(res);
                        } catch {
                          // Handle error
                        }
                      }
                    }}
                  >
                    <PlayCircle className="w-4 h-4" />
                  </button>

                  <button
                    className="text-blue-400 hover:text-blue-300"
                    title="Edit"
                    onClick={() => navigate(`/portal/agentStudio/${agent.id}`)}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>

                  {/* <button
                    className="text-yellow-400 hover:text-yellow-300"
                    title="Rename"
                    onClick={() => handleRenameClick(agent)}
                  >
                    <RocketIcon  className="w-4 h-4" />
                  </button> */}

                  <button className="text-red-400 hover:text-red-300" title="Delete" onClick={() => setConfirmDeleteAgentId(agent.id)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Evaluation Popup */}
        {showEvalPopup && (
          <CreditEvaluationPopup
            results={evalResults}
            onClose={() => {
              setShowEvalPopup(false);
              setEvalResults(null);
            }}
          />
        )}

        {/* Rename Modal */}
        {showRenameModal && renameTarget && (
          <RenameModal
            id={renameTarget.id}
            currentName={renameTarget.currentName}
            module="Agent"
            onCancel={() => setShowRenameModal(false)}
            onSave={handleSaveRename}
          />
        )}

        {showAddGroupModal && (
          <AddGroupModal
            onCancel={() => setShowAddGroupModal(false)}
            onSave={handleSaveGroup}
          />
        )}

        {confirmDeleteId && (
          <ConfirmDialog
            message="Are you sure you want to delete this agent group?"
            onYes={async () => {
              await handleDeleteGroup(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
            onCancel={() => setConfirmDeleteId(null)}
          />
        )}

        {confirmDeleteAgentId && (
          <ConfirmDialog
            message="Are you sure you want to delete this agent?"
            onYes={async () => {
              await handleDeleteAgent(confirmDeleteAgentId);
              setConfirmDeleteAgentId(null);
            }}
            onCancel={() => setConfirmDeleteAgentId(null)}
          />
        )}

      </div>
      {showAssignmentModal && assignmentModalAgentId && (
        <PermissionsAssignmentModal
          onClose={() => {
            setShowAssignmentModal(false);
            setAssignmentModalAgentId(null);
          }}
          onSave={() => {}}
          entityType="entity"
          entityName="Agent"
          module_id="d4c3b2a1-5e6f-4d3c-9b2a-3c4d5e6f7a8b"
          object_id={assignmentModalAgentId}
        />
      )}
    </div>
  );
};

export default Agents;
